import { createAsyncThunk } from "@reduxjs/toolkit";
import CaseDocumentsAPI, { AddDocumentRequest, AddDocumentResponse, DocumentsResponse, DownloadUrlParams, DownloadUrlResponse, GetDocumentUploadUrlParams, Document, DocumentStatus} from "../api/caseDocuments";
import { RootState } from "../app/store";

export const fetchCaseDocuments = createAsyncThunk<
  DocumentsResponse,
  { case_id: string },
  { state: RootState; rejectValue: void }
>(
  "caseDocuments/fetchCaseDocuments",
  async ({ case_id }, { rejectWithValue }) => {
    try {
      return await CaseDocumentsAPI.getDocuments(case_id);
    } catch (error) {
      console.error(error);
      return rejectWithValue();
    }
  },
  {
    condition: ({ case_id }, { getState }) => {
      const state = getState();
      const caseDocumentsState = state.caseDocuments;

      const isSameCase = caseDocumentsState.caseId === case_id;

      if (isSameCase) {
        return false;
      }
      
      return true;
    },
  }
);


export const addCaseDocument = createAsyncThunk<
  AddDocumentResponse,
  { case_id: string; payload: AddDocumentRequest; file: File },
  { rejectValue: void }
>(
  "caseDocuments/addCaseDocument",
  async ({ case_id, payload, file }, { rejectWithValue }) => {
    try {
      const uploadUrlParams: GetDocumentUploadUrlParams = {
        document_type: payload.document_type,
        file_name: payload.file_name
      }
      const uploadUrlResponse =  await CaseDocumentsAPI.getDocumentUploadUrl(case_id, uploadUrlParams);
      
      await CaseDocumentsAPI.uploadDocument(uploadUrlResponse.upload_url, file, uploadUrlResponse.upload_headers);

      const document: Document = {
        document_id: uploadUrlResponse.document_id,
        document_type: payload.document_type, 
        entity_type: payload.entity_type,
        entity_id: payload.entity_id,
        file_name: payload.file_name,
        file_type: payload.file_type,
        file_reference: uploadUrlResponse.file_reference,
        notes: payload.notes,
        created_at: new Date().toISOString(),
        status: DocumentStatus.ACTIVE
      }

      const response = await CaseDocumentsAPI.addDocument(case_id, document)

      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue();
    }
  }
);


export const replaceCaseDocument = createAsyncThunk<
  AddDocumentResponse,
  {
    case_id: string;
    document_id: string;
    payload: AddDocumentRequest;
    file: File;
  },
  { rejectValue: void }
>(
  "caseDocuments/replaceCaseDocument",
  async (
    { case_id, document_id, payload, file },
    { rejectWithValue }
  ) => {
    try {
      const uploadUrlParams: GetDocumentUploadUrlParams = {
        document_type: payload.document_type,
        file_name: payload.file_name
      }
      const uploadUrlResponse =  await CaseDocumentsAPI.getDocumentUploadUrl(case_id, uploadUrlParams);
      
      await CaseDocumentsAPI.uploadDocument(uploadUrlResponse.upload_url, file, uploadUrlResponse.upload_headers);

      const document: Document = {
        document_id: uploadUrlResponse.document_id,
        document_type: payload.document_type, 
        entity_type: payload.entity_type,
        entity_id: payload.entity_id,
        file_name: payload.file_name,
        file_type: payload.file_type,
        file_reference: uploadUrlResponse.file_reference,
        notes: payload.notes,
        created_at: new Date().toISOString(),
        status: DocumentStatus.ACTIVE
      }

      const response = await CaseDocumentsAPI.replaceDocument(
        case_id,
        document_id,
        document
      );

      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue();
    }
  }
);

export const deleteCaseDocument = createAsyncThunk<
  void,
  { case_id: string; document_id: string },
  { rejectValue: void }
>(
  "caseDocuments/deleteCaseDocument",
  async ({ case_id, document_id }, { rejectWithValue }) => {
    try {
      await CaseDocumentsAPI.deleteDocument(
        case_id,
        document_id
      );
    } catch (error) {
      console.error(error);
      return rejectWithValue();
    }
  }
);


export const fetchDocumentDownloadUrl = createAsyncThunk<
  DownloadUrlResponse,
  {
    document_id: string;
    params: DownloadUrlParams;
  },
  { rejectValue: void }
>(
  "caseDocuments/fetchDocumentDownloadUrl",
  async ({ params }, { rejectWithValue }) => {
    try {
      return await CaseDocumentsAPI.getDownloadUrl(params);
    } catch (error) {
      console.error(error);
      return rejectWithValue();
    }
  }
);

